"use client";

import { renderCmsContent } from "@/lib/utils";
import { useEffect, useState } from "react";

type TextData = {
  eyebrow?: string;
  heading?: string;
  subheading?: string;
  content?: string; // HTML/markdown — sanitized
  body?: string; // alias for content
  layout?: "centered" | "wide" | "prose";
};

export function TextSection({ data }: { data: TextData }) {
  const layout = data.layout ?? "prose";
  const raw = data.content ?? data.body ?? "";
  const [safeContent, setSafeContent] = useState("");

  useEffect(() => {
    let cancelled = false;
    void renderCmsContent(raw).then((html) => {
      if (!cancelled) setSafeContent(html);
    });
    return () => {
      cancelled = true;
    };
  }, [raw]);

  const isCentered = layout === "centered";
  const isWide = layout === "wide";

  const containerClass = isWide ? "mx-auto max-w-6xl" : "mx-auto max-w-3xl";
  const alignClass = isCentered ? "text-center" : "text-left";

  return (
    <section className="py-[var(--section-space)] px-4">
      <div className={containerClass}>
        {data.eyebrow ? (
          <p
            className={`mb-3 text-xs font-bold uppercase tracking-[0.2em] text-[var(--accent)] ${
              isCentered ? "text-center" : ""
            }`}
          >
            {data.eyebrow}
          </p>
        ) : null}
        {data.heading && (
          <h2
            className={`text-balance text-[clamp(1.75rem,3vw,2.5rem)] font-extrabold tracking-tight text-[var(--primary)] ${alignClass}`}
          >
            {data.heading}
          </h2>
        )}
        {data.subheading && (
          <p
            className={`mt-4 text-pretty text-base leading-relaxed text-[var(--muted-foreground)] md:text-lg ${
              isCentered ? "mx-auto max-w-3xl text-center" : ""
            }`}
          >
            {data.subheading}
          </p>
        )}
        {safeContent && (
          <div
            className={`prose-cms mt-6 text-[var(--foreground)] ${alignClass}`}
            dangerouslySetInnerHTML={{ __html: safeContent }}
          />
        )}
      </div>
    </section>
  );
}
